import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ClinicalSessionStatus, SaveSessionRequest, Step1Request, Step2Request, Step3Request, Step4Request } from '../../dtos/FlowClinicalSessions/index';
import { flowClinicalApi } from '../../services/flowClinicalApi';
import { clinicalSessionKeys } from '../ClinicalSession/useGetClinicalSession';

export const flowClinicalKeys = {
  all: ['flowClinical'] as const,
  session: (sessionId: string) => [...flowClinicalKeys.all, 'session', sessionId] as const,
  progress: (sessionId: string) => [...flowClinicalKeys.all, 'progress', sessionId] as const,
};

export function useGetFlowClinicalSession(sessionId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: flowClinicalKeys.session(sessionId),
    queryFn: async () => {
      const response = await flowClinicalApi.getSession(sessionId);
      if (!response.isSuccess) {
        toast.error(`Error: ${response.errorMessage || 'Failed to fetch clinical session'}`);
      }
      return response;
    },
    enabled: options?.enabled ?? !!sessionId,
  });
}

export function useGetFlowClinicalProgress(sessionId: string, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: flowClinicalKeys.progress(sessionId),
    queryFn: async () => {
      const response = await flowClinicalApi.getProgress(sessionId);
      if (!response.isSuccess) {
        toast.error(`Error: ${response.errorMessage || 'Failed to fetch progress'}`);
      }
      return response;
    },
    enabled: options?.enabled ?? !!sessionId,
  });
}

export function useProcessStep1() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Step1Request) => flowClinicalApi.processStep1(data),
    onSuccess: (response) => {
      if (response.isSuccess) {
        toast.success('Successfully completed step 1');
        queryClient.invalidateQueries({ queryKey: flowClinicalKeys.all });
        queryClient.invalidateQueries({ queryKey: clinicalSessionKeys.all });
      } else {
        toast.error(`Error: ${response.errorMessage || 'Failed to process step 1'}`);
      }
    },
    onError: (error: Error) => {
      toast.error(`Error: ${error.message || 'Failed to process step 1'}`);
    },
  });
}

export function useProcessStep2() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Step2Request) => flowClinicalApi.processStep2(data),
    onSuccess: (response) => {
      if (response.isSuccess) {
        toast.success('Successfully completed step 2');
        queryClient.invalidateQueries({ queryKey: flowClinicalKeys.all });
        queryClient.invalidateQueries({ queryKey: clinicalSessionKeys.all });
      } else {
        toast.error(`Error: ${response.errorMessage || 'Failed to process step 2'}`);
      }
    },
    onError: (error: Error) => {
      toast.error(`Error: ${error.message || 'Failed to process step 2'}`);
    },
  });
}

export function useProcessStep3() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Step3Request) => flowClinicalApi.processStep3(data),
    onSuccess: (response) => {
      if (response.isSuccess) {
        toast.success('Rezultatele au fost calculate cu succes');
        queryClient.invalidateQueries({ queryKey: flowClinicalKeys.all });
        queryClient.invalidateQueries({ queryKey: clinicalSessionKeys.all });
      } else {
        toast.error(`Error: ${response.errorMessage || 'Failed to process step 3'}`);
      }
    },
    onError: (error: unknown) => {
      const axiosError = error as {
        response?: { data?: { errorMessage?: string } };
        message?: string;
      };
      const errorMessage =
        axiosError?.response?.data?.errorMessage ||
        axiosError?.message ||
        'Failed to process step 3';
      toast.error(`Error: ${errorMessage}`);
    },
  });
}

export function useProcessStep4() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: Step4Request) => flowClinicalApi.processStep4(data),
    onSuccess: (response) => {
      if (response.isSuccess) {
        toast.success('Sesiunea a fost finalizată cu succes');
        queryClient.invalidateQueries({ queryKey: flowClinicalKeys.all });
        queryClient.invalidateQueries({ queryKey: clinicalSessionKeys.all });
      } else {
        toast.error(`Error: ${response.errorMessage || 'Failed to process step 4'}`);
      }
    },
    onError: (error: Error) => {
      toast.error(`Error: ${error.message || 'Failed to process step 4'}`);
    },
  });
}

/**
 * Hook to save session progress.
 * If SessionId is empty or session doesn't exist, creates a new session.
 */
export function useSaveSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: SaveSessionRequest) => flowClinicalApi.saveSession(data),
    onSuccess: (response) => {
      if (response.isSuccess) {
        // Check if it's a new session by status (Draft typically indicates new session)
        const isNew = response.data?.status === ClinicalSessionStatus.Draft;
        toast.success(isNew ? 'Sesiune nouă creată cu succes' : 'Sesiune salvată cu succes');
        queryClient.invalidateQueries({ queryKey: flowClinicalKeys.all });
        queryClient.invalidateQueries({ queryKey: clinicalSessionKeys.all });
      } else {
        toast.error(`Error: ${response.errorMessage || 'Failed to save session'}`);
      }
    },
    onError: (error: unknown) => {
      // Check for specific HTTP errors
      const axiosError = error as { response?: { status?: number } };
      if (axiosError?.response?.status === 405) {
        toast.error('Endpoint-ul de salvare nu este disponibil. Verificați dacă backend-ul este actualizat.');
      } else {
        const errorMessage = error instanceof Error ? error.message : 'Failed to save session';
        toast.error(`Error: ${errorMessage}`);
      }
    },
  });
}
